import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { DeviceSummary } from '@irrigazione/shared';
import { api } from '../api/client';
import { Badge, Meter, Notice, StatusDot, batteryColor, moistureColor, waterColor } from '../components/ui';
import { formatAgo, formatWhen } from '../format';

/** Durata dell'irrigazione lanciata dal pulsante rapido della dashboard. */
const QUICK_MINUTES = 5;

export function Dashboard() {
  const devices = useQuery({ queryKey: ['devices'], queryFn: api.devices });
  const gateways = useQuery({ queryKey: ['gateways'], queryFn: api.gateways });

  if (devices.isLoading) return <p className="muted">Caricamento…</p>;
  if (devices.error) return <Notice variant="error">{(devices.error as Error).message}</Notice>;

  const list = devices.data ?? [];
  const offlineGateways = (gateways.data ?? []).filter((g) => !g.online);

  return (
    <div className="stack">
      {offlineGateways.length > 0 && (
        <Notice variant="warning">
          {offlineGateways.length === 1
            ? `Il gateway "${offlineGateways[0]!.name}" non risponde: i nodi LoRa non sono raggiungibili.`
            : `${offlineGateways.length} gateway non rispondono: i nodi LoRa non sono raggiungibili.`}
        </Notice>
      )}

      {list.length === 0 ? (
        <Notice>
          Nessun irrigatore registrato. Accendi un controller: si registra da solo alla prima
          connessione e compare qui.
        </Notice>
      ) : (
        <div className="grid">
          {list.map((device) => (
            <DeviceCard key={device.id} device={device} />
          ))}
        </div>
      )}
    </div>
  );
}

function DeviceCard({ device }: { device: DeviceSummary }) {
  const queryClient = useQueryClient();
  const reading = device.lastReading;
  const threshold = device.config.moistureThresholdPct;

  const irrigate = useMutation({
    mutationFn: () => api.irrigate(device.id, QUICK_MINUTES),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['devices'] }),
  });

  return (
    <article className="card device-card">
      <header className="device-card__head">
        <StatusDot online={device.online} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Link to={`/devices/${device.id}`} className="device-card__title">
            {device.name}
          </Link>
          <div className="tiny muted">
            {device.online ? `visto ${formatAgo(device.lastSeenAt)}` : `offline · ${formatAgo(device.lastSeenAt)}`}
          </div>
        </div>
        <Badge>{device.type === 'standalone' ? 'Standalone' : `LoRa · nodo ${device.nodeId}`}</Badge>
        {!device.enabled && <Badge>Sospeso</Badge>}
      </header>

      <div className="stack" style={{ gap: 10 }}>
        <Meter
          label="Umidità terreno"
          value={reading?.moisturePct ?? null}
          color={moistureColor(reading?.moisturePct ?? null, threshold)}
          threshold={threshold}
        />
        <Meter
          label="Batteria"
          value={reading?.batteryPct ?? null}
          color={batteryColor(reading?.batteryPct ?? null)}
        />
        {device.type === 'standalone' && (
          <Meter
            label="Serbatoio"
            value={reading?.waterLevelPct ?? null}
            color={waterColor(reading?.waterLevelPct ?? null)}
          />
        )}
      </div>

      <div className="row row-wrap" style={{ justifyContent: 'space-between' }}>
        <span className="tiny secondary">
          {device.irrigating ? (
            <strong>Irrigazione in corso</strong>
          ) : !device.enabled ? (
            'Sospeso: non irriga'
          ) : device.config.mode === 'off' ? (
            'Automatismi disattivati'
          ) : device.nextIrrigationAt ? (
            `Prossima: ${formatWhen(device.nextIrrigationAt)}`
          ) : (
            'Nessuna irrigazione programmata'
          )}
        </span>
        {/* Gli standalone si avviano a mano solo dal pulsante sul controller. */}
        {device.type === 'lora' ? (
          <button
            className="btn btn--sm"
            onClick={() => irrigate.mutate()}
            disabled={irrigate.isPending || !device.enabled}
          >
            Irriga {QUICK_MINUTES} min
          </button>
        ) : (
          <span className="tiny muted">Avvio manuale dal pulsante</span>
        )}
      </div>

      {device.pendingCommands > 0 && (
        <p className="tiny muted" style={{ margin: 0 }}>
          {device.type === 'standalone'
            ? 'Comando in attesa: partirà al prossimo risveglio del controller.'
            : 'Comando in attesa di consegna al nodo.'}
        </p>
      )}
      {irrigate.error && <p className="tiny" style={{ margin: 0, color: 'var(--status-critical)' }}>
        {(irrigate.error as Error).message}
      </p>}
    </article>
  );
}
